import { HeadingTagType } from "@lexical/rich-text";
import {
  COMMAND_PRIORITY_NORMAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_MODIFIER_COMMAND,
  LexicalEditor,
  OUTDENT_CONTENT_COMMAND,
} from "lexical";
import { useEffect } from "react";

import { useSettings } from "../../context/SettingsContext";
import { useToolbarState } from "../../context/ToolbarContext";
import {
  clearFormatting,
  formatBulletList,
  formatCheckList,
  formatHeading,
  formatNumberedList,
  formatParagraph,
  formatQuote,
  updateFontSize,
  UpdateFontSizeType,
} from "../ToolbarPlugin/utils";
import {
  isCenterAlign,
  isClearFormatting,
  isDecreaseFontSize,
  isFormatBulletList,
  isFormatCheckList,
  isFormatHeading,
  isFormatNumberedList,
  isFormatParagraph,
  isFormatQuote,
  isIncreaseFontSize,
  isIndent,
  isInsertCodeBlock,
  isJustifyAlign,
  isLeftAlign,
  isOutdent,
  isRightAlign,
  isStrikeThrough,
  isSubscript,
  isSuperscript,
} from "./shortcuts";

export default function ShortcutsPlugin({
  editor,
}: {
  editor: LexicalEditor;
}): null {
  const { toolbarState } = useToolbarState();
  const { settings } = useSettings();

  useEffect(() => {
    const keyboardShortcutsHandler = (payload: KeyboardEvent) => {
      const event: KeyboardEvent = payload;

      if (isFormatParagraph(event)) {
        event.preventDefault();
        formatParagraph(editor);
        return true;
      }

      if (isFormatHeading(event) && settings.enableBlockFormat) {
        event.preventDefault();
        const { code } = event;
        const headingSize = `h${code[code.length - 1]}` as HeadingTagType;
        formatHeading(editor, toolbarState.blockType, headingSize);
        return true;
      }

      if (isFormatBulletList(event) && settings.enableBlockFormat) {
        event.preventDefault();
        formatBulletList(editor, toolbarState.blockType);
        return true;
      }

      if (isFormatNumberedList(event) && settings.enableBlockFormat) {
        event.preventDefault();
        formatNumberedList(editor, toolbarState.blockType);
        return true;
      }

      if (isFormatCheckList(event) && settings.enableBlockFormat) {
        event.preventDefault();
        formatCheckList(editor, toolbarState.blockType);
        return true;
      }

      if (isFormatQuote(event) && settings.enableBlockFormat) {
        event.preventDefault();
        formatQuote(editor, toolbarState.blockType);
        return true;
      }

      if (isIndent(event) && settings.enableElementFormat) {
        event.preventDefault();
        editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        return true;
      }

      if (isOutdent(event) && settings.enableElementFormat) {
        event.preventDefault();
        editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        return true;
      }

      if (isCenterAlign(event) && settings.enableElementFormat) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
        return true;
      }

      if (isLeftAlign(event) && settings.enableElementFormat) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
        return true;
      }

      if (isRightAlign(event) && settings.enableElementFormat) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
        return true;
      }

      if (isJustifyAlign(event) && settings.enableElementFormat) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
        return true;
      }

      if (isSuperscript(event)) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "superscript");
        return true;
      }

      if (isStrikeThrough(event) && settings.enableTextFormat) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
        return true;
      }

      if (isSubscript(event) && settings.enableTextFormat) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "subscript");
        return true;
      }

      if (isInsertCodeBlock(event) && settings.enableCode) {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
        return true;
      }

      if (isIncreaseFontSize(event) && settings.enableFontSize) {
        event.preventDefault();
        updateFontSize(
          editor,
          UpdateFontSizeType.increment,
          toolbarState.fontSizeInputValue
        );
        return true;
      }

      if (isDecreaseFontSize(event) && settings.enableFontSize) {
        event.preventDefault();
        updateFontSize(
          editor,
          UpdateFontSizeType.decrement,
          toolbarState.fontSizeInputValue
        );
        return true;
      }

      if (isClearFormatting(event)) {
        event.preventDefault();
        clearFormatting(editor);
      }

      return false;
    };

    const unregisterKeyboardShortcut = editor.registerCommand(
      KEY_MODIFIER_COMMAND,
      keyboardShortcutsHandler,
      COMMAND_PRIORITY_NORMAL
    );

    const unregisterTextFormatGuard = editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      (event) => {
        if (!settings.enableTextFormat) {
          // block lexical's built-in font style listener by returning true, see: https://lexical.dev/docs/concepts/commands
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );

    return () => {
      unregisterKeyboardShortcut();
      unregisterTextFormatGuard();
    };
  }, [
    editor,
    toolbarState.isLink,
    toolbarState.blockType,
    toolbarState.fontSizeInputValue,
    settings,
  ]);

  return null;
}
